import React, { useState, useEffect, useCallback } from 'react';
import { Fuel, RefreshCw, Check, AlertTriangle, ExternalLink, Loader2, Info, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import axiosInstance from '../utils/axiosInstance';
import ShippyLoader from '../components/ShippyLoader';
import CarrierLogo from '../components/CarrierLogo';

const pct = (p) => (p === null || p === undefined) ? '–' : `${(p * 100).toFixed(2)}%`;
const dstr = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '';
const dts = (d) => d ? new Date(d).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';
const period = (r) => {
  if (!r) return '';
  if (r.period_end && r.period_end !== r.period_start) return `${dstr(r.period_start)} – ${dstr(r.period_end)}`;
  const d = new Date(r.period_start);
  return r.period_end ? dstr(r.period_start) : d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
};
const valuesText = (values) => {
  const e = Object.entries(values || {});
  if (!e.length) return 'no lanes';
  return e.map(([v, n]) => `${n} at ${(parseFloat(v) * 100).toFixed(2)}%`).join(', ');
};

/** Tiny SVG history line: the index over its last readings, newest on the right. */
function Spark({ history, lane }) {
  const pts = [...(history || [])].reverse();
  if (pts.length < 2) return <div className="h-10 text-[11px] text-muted-foreground flex items-center">history builds up week by week</div>;
  const w = 220, h = 40, pad = 3;
  const ys = pts.map(p => p.pct);
  const all = lane !== null && lane !== undefined ? [...ys, lane] : ys;
  const lo = Math.min(...all), hi = Math.max(...all);
  const y = (v) => hi === lo ? h / 2 : h - pad - ((v - lo) / (hi - lo)) * (h - 2 * pad);
  const x = (i) => pad + (i / (pts.length - 1)) * (w - 2 * pad);
  const d = pts.map((p, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(p.pct).toFixed(1)}`).join(' ');
  return (
    <svg width={w} height={h} className="block" aria-label="index history">
      {lane !== null && lane !== undefined && (
        <line x1={pad} x2={w - pad} y1={y(lane)} y2={y(lane)} stroke="currentColor" strokeOpacity="0.35" strokeDasharray="3 3" />
      )}
      <path d={d} fill="none" stroke="currentColor" strokeWidth="1.6" className="text-orange-600" />
      <circle cx={x(pts.length - 1)} cy={y(pts[pts.length - 1].pct)} r="2.6" className="fill-orange-600" />
    </svg>
  );
}

function DriftBadge({ s }) {
  if (!s.current) return <span className="rounded px-1.5 py-0.5 text-[11px] bg-muted text-muted-foreground">no reading yet</span>;
  if (s.state.lanes === 0 && s.state.pickup === 0) return <span className="rounded px-1.5 py-0.5 text-[11px] bg-muted text-muted-foreground">no lanes mapped</span>;
  if (s.drift.on_index) return <span className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] bg-emerald-100 text-emerald-800"><Check className="h-3 w-3" /> lanes on the index</span>;
  const g = s.drift.max_gap_pts;
  return (
    <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] ${Math.abs(g) >= 2 ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-900'}`}>
      <AlertTriangle className="h-3 w-3" /> {s.drift.lanes_off} lane{s.drift.lanes_off === 1 ? '' : 's'} {g > 0 ? 'under' : 'over'} by {Math.abs(g).toFixed(2)} pt
    </span>
  );
}

function SourceCard({ s, onFetch, onApply, onToggle, onPaste, busy }) {
  const [customPct, setCustomPct] = useState('');
  const [pasteText, setPasteText] = useState('');
  const laneValues = Object.keys(s.state.values || {});
  const laneOne = laneValues.length === 1 ? parseFloat(laneValues[0]) : null;
  return (
    <div className="rounded-lg border border-border bg-card p-4 flex flex-col gap-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <CarrierLogo name={s.brand} height={22} showName={false} />
          <div className="min-w-0">
            <div className="font-semibold leading-tight truncate">{s.name}</div>
            <div className="text-[11px] text-muted-foreground">{s.cadence} · {s.applies_to}</div>
          </div>
        </div>
        <a href={s.url} target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-foreground shrink-0" title="Carrier page">
          <ExternalLink className="h-4 w-4" />
        </a>
      </div>

      <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 items-end">
        <div>
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Published index</div>
          <div className="text-2xl font-semibold tabular-nums leading-none">{pct(s.current?.pct)}</div>
          {s.current && s.target_pct !== null && s.target_pct !== undefined && Math.abs(s.target_pct - s.current.pct) > 0.00001 && (
            <div className="text-[11px] text-orange-700 mt-1" title="What the lanes should carry for this index">
              {s.factor !== 1 ? `× ${Math.round(s.factor * 100)}% contract` : ''}{s.factor !== 1 && s.offset ? ' ' : ''}{s.offset ? `+ ${(s.offset * 100).toFixed(2)} pt` : ''} = <b>{pct(s.target_pct)}</b>{s.target_field === 'surcharge_pct' ? ' extra cost on the base' : ' on our lanes'}
            </div>
          )}
          <div className="text-[11px] text-muted-foreground mt-1">
            {s.current ? period(s.current) : 'not read yet'}
            {s.previous && s.current && Math.abs(s.previous.pct - s.current.pct) > 0.00001 && (
              <span className="ml-1">(was {pct(s.previous.pct)})</span>
            )}
          </div>
          {s.next && (
            <div className="text-[11px] mt-0.5 inline-flex items-center gap-1 text-orange-700">
              <ArrowRight className="h-3 w-3" /> {pct(s.next.pct)} from {dstr(s.next.period_start)}
            </div>
          )}
        </div>
        <div className="text-muted-foreground"><Spark history={s.history} lane={laneOne} /></div>
      </div>

      <div className="rounded-md bg-muted/50 p-2.5 text-xs space-y-1">
        <div className="flex items-center justify-between gap-2">
          <span className="font-medium">Shippy lanes{s.state.target_field === 'surcharge_pct' ? ' (extra cost %)' : ''}</span>
          <DriftBadge s={s} />
        </div>
        <div className="text-muted-foreground">
          {s.state.lanes} lane{s.state.lanes === 1 ? '' : 's'}: {valuesText(s.state.values)}{s.fetch_via === 'scrapingfish' ? ' · read via ScrapingFish' : ''}
          {s.state.fixed_lanes > 0 && <span> · {s.state.fixed_lanes} fixed €/parcel (untouched)</span>}
          {s.state.pickup > 0 && <span> · pickup {valuesText(s.state.pickup_values)}</span>}
        </div>
        <div className="text-muted-foreground truncate" title={(s.state.carriers || []).join(', ')}>
          {(s.state.carriers || []).join(', ')}{s.state.countries?.length ? ` · ${s.state.countries.length} countries` : ''}
        </div>
        {s.impact_30d !== null && s.impact_30d !== undefined && !s.drift.on_index && (
          <div className={s.impact_30d > 0 ? 'text-red-700' : 'text-emerald-700'}>
            Drift worth about €{Math.abs(s.impact_30d).toLocaleString('en-GB')}/month {s.impact_30d > 0 ? 'of cost we do not see' : 'we over-charge ourselves'} (last 30 days of orders)
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 text-xs">
        <Button size="sm" variant="outline" disabled={busy} onClick={() => onFetch(s.key)} title="Read the carrier page now">
          {busy === `fetch:${s.key}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          <span className="ml-1">Fetch</span>
        </Button>
        <Button size="sm" disabled={busy || !s.current || s.drift.on_index} onClick={() => onApply(s.key, null)}
          title="Write the published index onto every lane above (and announce it in Slack)">
          {busy === `apply:${s.key}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
          <span className="ml-1">Apply {pct(s.target_pct ?? s.current?.pct)}</span>
        </Button>
        <label className="inline-flex items-center gap-1.5 ml-auto cursor-pointer select-none" title="The daily sync writes new index values onto the lanes by itself">
          <input type="checkbox" checked={!!s.auto_apply} disabled={busy} onChange={(e) => onToggle(s.key, { auto_apply: e.target.checked })} />
          auto-apply
        </label>
      </div>
      <details className="text-xs" open={!!s.last_error}>
        <summary className="cursor-pointer text-muted-foreground">Paste the page text</summary>
        <div className="mt-1.5 space-y-1.5">
          <textarea className="w-full h-20 rounded border border-border bg-background px-2 py-1 text-xs font-mono" placeholder="Select all on the carrier's page, copy, paste here. The same reader runs on it."
            value={pasteText} onChange={(e) => setPasteText(e.target.value)} />
          <Button size="sm" variant="outline" disabled={busy || pasteText.length < 20} onClick={() => { onPaste(s.key, pasteText); setPasteText(''); }}>Read the pasted text</Button>
        </div>
      </details>
      <details className="text-xs">
        <summary className="cursor-pointer text-muted-foreground">Apply another value</summary>
        <div className="mt-1.5 flex items-center gap-2">
          <input className="w-24 rounded border border-border bg-background px-2 py-1 text-xs" placeholder="e.g. 45.00" value={customPct}
            onChange={(e) => setCustomPct(e.target.value)} />
          <span className="text-muted-foreground">%</span>
          <Button size="sm" variant="outline" disabled={busy || !customPct} onClick={() => { onApply(s.key, parseFloat(customPct) / 100); setCustomPct(''); }}>Apply</Button>
        </div>
      </details>

      <div className="text-[11px] text-muted-foreground flex flex-wrap gap-x-3">
        <span>fetched {s.last_fetched_at ? dts(s.last_fetched_at) : 'never'}</span>
        {s.last_change && <span>last change {dts(s.last_change.applied_at)} by {s.last_change.applied_by} → {pct(s.last_change.to_pct)}</span>}
      </div>
      {s.last_error && (
        <div className="rounded border border-red-200 bg-red-50 text-red-800 px-2 py-1.5 text-xs flex gap-1.5">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" /> <span className="break-all">{s.last_error}</span>
        </div>
      )}
      {s.notes && <div className="text-[11px] text-muted-foreground whitespace-pre-line">{s.notes}</div>}
    </div>
  );
}

export default function FuelIndex() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ sources: [], changes: [] });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [flash, setFlash] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await axiosInstance.get('api/fuel-index/');
      setData(r.data || { sources: [], changes: [] });
      setError('');
    } catch (e) {
      setError(`Could not load the fuel index (${e?.response?.status || e.message}).`);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const fetchNow = async (key) => {
    setBusy(`fetch:${key || 'all'}`);
    try {
      const r = await axiosInstance.post('api/fuel-index/fetch/', key ? { key } : {});
      const ev = (r.data?.events || []);
      const bad = ev.filter(e => e.error);
      setFlash(bad.length ? `Fetch done, ${bad.length} failed: ${bad.map(b => b.key).join(', ')}` : `Fetched ${ev.length} source${ev.length === 1 ? '' : 's'}${ev.some(e => e.moved) ? ', an index moved' : ''}`);
      await load();
    } catch (e) {
      setFlash(`Fetch failed (${e?.response?.status || e.message})`);
    } finally { setBusy(''); }
  };
  const apply = async (key, p) => {
    const s = data.sources.find(x => x.key === key);
    const target = p === null ? (s?.target_pct ?? s?.current?.pct) : p;
    if (!window.confirm(`Apply ${pct(target)} to the ${s?.state?.lanes || 0} lane(s) of ${s?.name}? Checkout prices on these lanes change within 10 minutes, and the change is announced in Slack.`)) return;
    setBusy(`apply:${key}`);
    try {
      const r = await axiosInstance.post('api/fuel-index/apply/', { key, pct: p === null ? undefined : p });
      const c = r.data?.change;
      setFlash(`Applied ${pct(c?.to_pct)} to ${c?.lanes_updated} lanes${c?.pickup_updated ? ` + ${c.pickup_updated} pickup rows` : ''}${r.data?.slack === false ? ' (Slack post failed)' : r.data?.slack === null ? ' (no Slack channel configured)' : ', announced in Slack'}`);
      await load();
    } catch (e) {
      setFlash(`Apply failed: ${e?.response?.data?.error || e.message}`);
    } finally { setBusy(''); }
  };
  const paste = async (key, text) => {
    setBusy(`paste:${key}`);
    try {
      const r = await axiosInstance.post('api/fuel-index/paste/', { key, text });
      setFlash(`Read ${r.data?.new} new period${r.data?.new === 1 ? '' : 's'} from the pasted text${r.data?.updated ? ` (${r.data.updated} updated)` : ''}`);
      await load();
    } catch (e) {
      setFlash(`Could not read the pasted text: ${e?.response?.data?.error || e.message}`);
    } finally { setBusy(''); }
  };
  const toggle = async (key, patch) => {
    setBusy(`toggle:${key}`);
    try { await axiosInstance.put(`api/fuel-index/sources/${key}/`, patch); await load(); }
    catch (e) { setFlash(`Could not save (${e?.response?.status || e.message})`); }
    finally { setBusy(''); }
  };

  if (loading) return <ShippyLoader message="Reading the fuel indexes" />;

  const drifting = data.sources.filter(s => s.current && !s.drift.on_index && (s.state.lanes || s.state.pickup));
  return (
    <div className="p-3 md:p-6 space-y-5 max-w-[1400px]">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold flex items-center gap-2"><Fuel className="h-5 w-5" /> Fuel Index</h1>
          <p className="text-sm text-muted-foreground max-w-3xl">
            What each carrier publishes as fuel surcharge, against what our lanes carry. The daily sync reads the pages
            (DHL Express changes weekly, the parcel carriers monthly), posts every move to Slack and, where auto-apply is on,
            writes the new value onto the lanes itself. A change here reaches checkout within 10 minutes.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" disabled={!!busy} onClick={() => fetchNow(null)}>
            {busy === 'fetch:all' ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            <span className="ml-1">Fetch all now</span>
          </Button>
        </div>
      </div>

      {error && <div className="rounded border border-red-200 bg-red-50 text-red-800 px-3 py-2 text-sm">{error}</div>}
      {flash && <div className="rounded border border-border bg-muted/50 px-3 py-2 text-sm flex items-center gap-2"><Info className="h-4 w-4" /> {flash}</div>}

      {drifting.length > 0 && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 text-amber-950 px-4 py-3 text-sm">
          <div className="font-medium">{drifting.length} source{drifting.length === 1 ? '' : 's'} off the index</div>
          <ul className="mt-1 space-y-0.5">
            {drifting.map(s => (
              <li key={s.key}>
                {s.name}: index {pct(s.current.pct)}, lanes {valuesText(s.state.values)}
                {s.impact_30d ? ` (about €${Math.abs(s.impact_30d).toLocaleString('en-GB')}/month)` : ''}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {data.sources.map(s => (
          <SourceCard key={s.key} s={s} busy={busy} onFetch={fetchNow} onApply={apply} onToggle={toggle} onPaste={paste} />
        ))}
        {data.sources.length === 0 && (
          <div className="text-sm text-muted-foreground">No sources yet. Seed them with <code>manage.py sync_fuel_indexes --seed</code>.</div>
        )}
      </div>

      <div>
        <h2 className="text-base font-semibold mb-2">Changes done</h2>
        {data.changes.length === 0 ? (
          <div className="text-sm text-muted-foreground">No lane has been changed from here yet.</div>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-xs">
              <thead className="bg-muted/50 text-left">
                <tr>
                  <th className="px-3 py-2 font-semibold">When</th>
                  <th className="px-3 py-2 font-semibold">Source</th>
                  <th className="px-3 py-2 font-semibold">From</th>
                  <th className="px-3 py-2 font-semibold">To</th>
                  <th className="px-3 py-2 font-semibold text-right">Lanes</th>
                  <th className="px-3 py-2 font-semibold">By</th>
                  <th className="px-3 py-2 font-semibold">Slack</th>
                  <th className="px-3 py-2 font-semibold">Note</th>
                </tr>
              </thead>
              <tbody>
                {data.changes.map(c => (
                  <tr key={c.id} className="border-t border-border/60">
                    <td className="px-3 py-1.5 whitespace-nowrap">{dts(c.applied_at)}</td>
                    <td className="px-3 py-1.5">{c.source_name}</td>
                    <td className="px-3 py-1.5 text-muted-foreground">{valuesText(c.from_values?.lanes)}</td>
                    <td className="px-3 py-1.5 font-medium tabular-nums">{pct(c.to_pct)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{c.lanes_updated}{c.pickup_updated ? ` +${c.pickup_updated} pickup` : ''}</td>
                    <td className="px-3 py-1.5">{c.applied_by}</td>
                    <td className="px-3 py-1.5">{c.slack_ok ? 'posted' : '–'}</td>
                    <td className="px-3 py-1.5 text-muted-foreground">{c.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="text-[11px] text-muted-foreground">
        Slack channel for the announcements: {data.slack_channel || 'not configured (set SLACK_FUEL_INDEX_CHANNEL)'} · cron: <code>manage.py sync_fuel_indexes</code> daily at 20:00 (after the price scraper's ScrapingFish run, which holds the API's only slot until about 15:00).
      </div>
    </div>
  );
}
